import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Coins, Loader2, ArrowLeft, CheckCircle2, ShoppingCart, History, Infinity as InfinityIcon } from 'lucide-react';
import { api } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import { toast } from 'sonner';

/**
 * /billing — user-facing balance page.
 *
 * Three stacked sections: Balance, Packs, History. No tabs needed.
 * After returning from Stripe Checkout (?session_id=cs_test_xxx) we poll
 * /billing/checkout-status for up to 20 s to bridge any webhook latency,
 * then surface a success toast and refresh the balance.
 */
export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { balance, isUnlimited, refresh, openBuyModal } = useCredits();
  const [packs, setPacks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [lifetime, setLifetime] = useState({ purchased: 0, spent: 0 });
  const [loading, setLoading] = useState(true);
  const [pollingSession, setPollingSession] = useState(null);
  const [pollingResult, setPollingResult] = useState(null);
  const [busyPack, setBusyPack] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [packRes, balRes, txRes] = await Promise.all([
        api.get('/billing/packs'),
        api.get('/billing/balance'),
        api.get('/billing/transactions?limit=50'),
      ]);
      setPacks(packRes.data.packs || []);
      setLifetime({
        purchased: balRes.data.lifetime_credits_purchased || 0,
        spent: balRes.data.lifetime_credits_spent || 0,
      });
      setTransactions(txRes.data.transactions || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Post-Stripe-return polling (up to 20 s, every 2 s).
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (cancelled) {
      toast('Checkout cancelled — no credits were added.');
      return;
    }
    if (!sessionId || pollingSession) return;

    setPollingSession(sessionId);
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/billing/checkout-status/${sessionId}`);
        if (res.data.credits_applied) {
          setPollingResult({ ok: true, credits: res.data.credits });
          setPollingSession(null);
          await refresh();
          await loadAll();
          toast.success(`+${res.data.credits.toLocaleString()} credits added!`);
          return;
        }
      } catch (_) { /* keep polling */ }
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPollingSession(null);
        setPollingResult({ ok: false });
        toast.error('Still processing — refresh this page in a moment.');
      }
    };
    poll();
  }, [searchParams]);

  const handlePurchase = async (packId) => {
    setBusyPack(packId);
    try {
      const res = await api.post('/billing/checkout', {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) { window.location.href = res.data.url; return; }
    } catch (_) { toast.error('Could not start checkout.'); }
    finally { setBusyPack(null); }
  };

  const kindStyles = {
    purchase: { tone: 'text-emerald-700', prefix: '+', badge: 'Purchase' },
    grant:    { tone: 'text-emerald-700', prefix: '+', badge: 'Grant' },
    refund:   { tone: 'text-sky-700',     prefix: '+', badge: 'Refund' },
    spend:    { tone: 'text-slate-700',   prefix: '',  badge: 'Spend' },
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b1220] transition-colors duration-200 pb-12">
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
          data-testid="billing-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        {/* Balance card */}
        <Card className="mb-5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Coins className="h-5 w-5 text-amber-600" />
              Your credit balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isUnlimited ? (
              <div className="flex items-center gap-3" data-testid="billing-unlimited">
                <InfinityIcon className="h-8 w-8 text-emerald-600" />
                <div>
                  <p className="text-2xl font-bold text-emerald-700">Unlimited</p>
                  <p className="text-xs text-slate-500">Admin / unlimited account — no credits deducted.</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-8">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Available</p>
                  <p className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="billing-balance">
                    {(balance ?? 0).toLocaleString()} cr
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Lifetime purchased</p>
                  <p className="text-lg font-semibold text-emerald-700">
                    {lifetime.purchased.toLocaleString()} cr
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Lifetime spent</p>
                  <p className="text-lg font-semibold text-slate-700">
                    {lifetime.spent.toLocaleString()} cr
                  </p>
                </div>
                <div className="ml-auto">
                  <Button onClick={() => openBuyModal()} data-testid="billing-topup-btn">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Add credits
                  </Button>
                </div>
              </div>
            )}
            {pollingSession && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-800">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Processing your payment… your credits will appear shortly.
              </div>
            )}
            {pollingResult?.ok && (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
                <CheckCircle2 className="h-4 w-4" />
                +{pollingResult.credits.toLocaleString()} credits added to your balance.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Packs */}
        {!isUnlimited && (
          <Card className="mb-5" data-testid="billing-packs-card">
            <CardHeader>
              <CardTitle className="text-lg">Credit packs</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8 text-slate-500">
                  <Loader2 className="animate-spin h-5 w-5" />
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-3">
                  {packs.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`billing-pack-${p.id}`}
                      className={`relative flex flex-col rounded-xl border p-4 ${
                        p.badge
                          ? 'border-amber-300 bg-amber-50 ring-2 ring-amber-200'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      {p.badge && (
                        <span className="absolute -top-2 right-4 inline-flex items-center rounded-full bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5">
                          {p.badge}
                        </span>
                      )}
                      <div className="text-xs text-slate-500 uppercase tracking-wide">{p.name}</div>
                      <div className="mt-1 text-2xl font-bold text-slate-900">
                        {p.credits.toLocaleString()} cr
                      </div>
                      <div className="mt-1 text-sm text-slate-600">${p.price_usd.toFixed(0)}</div>
                      {p.tagline && <p className="mt-2 text-[11px] text-slate-500">{p.tagline}</p>}
                      <Button
                        className="mt-3 w-full"
                        onClick={() => handlePurchase(p.id)}
                        disabled={busyPack !== null}
                        data-testid={`billing-buy-${p.id}`}
                      >
                        {busyPack === p.id ? <Loader2 className="animate-spin h-4 w-4" /> : 'Buy'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card data-testid="billing-history-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <History className="h-5 w-5 text-slate-500" />
              Transaction history
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No transactions yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {transactions.map((tx) => {
                  const style = kindStyles[tx.kind] || kindStyles.spend;
                  const date = new Date(tx.created_at).toLocaleString();
                  return (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between py-2.5 text-sm"
                      data-testid={`tx-row-${tx.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {style.badge}
                          </span>
                          <span className="truncate text-slate-800">
                            {tx.metadata?.note ||
                              (tx.feature === 'purchase'
                                ? `Pack: ${tx.metadata?.pack_id || '—'}`
                                : tx.feature)}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{date}</div>
                      </div>
                      <div className={`font-semibold ${style.tone}`}>
                        {style.prefix}{Math.abs(tx.amount).toLocaleString()} cr
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
